import {
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { ItemStatus, PaginatedItemStatusResponse } from "../../../types/admin";

interface ItemStatusTableProps {
  data?: PaginatedItemStatusResponse;
  isLoading: boolean;
  error: any;
  selectedStatuses: number[];
  onSelectionChange: (selectedIds: number[]) => void;
  onView: (status: ItemStatus) => void;
  onEdit: (status: ItemStatus) => void;
  onDelete: (id: number) => void;
  onToggleActive: (id: number) => void;
  onReorder: (statuses: ItemStatus[]) => void;
  onRefresh: () => void;
}

const ItemStatusTable: React.FC<ItemStatusTableProps> = ({
  data,
  isLoading,
  error,
  selectedStatuses,
  onSelectionChange,
  onView,
  onEdit,
  onDelete,
  onToggleActive,
  onReorder,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<{
    [key: number]: HTMLElement | null;
  }>({});
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    statusId: number,
  ) => {
    setAnchorEl({ ...anchorEl, [statusId]: event.currentTarget });
  };

  const handleMenuClose = (statusId: number) => {
    setAnchorEl({ ...anchorEl, [statusId]: null });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      onSelectionChange(data?.statuses.map((status) => status.id) || []);
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectStatus = (statusId: number, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedStatuses, statusId]);
    } else {
      onSelectionChange(selectedStatuses.filter((id) => id !== statusId));
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const statuses = data?.statuses || [];
    const draggedStatus = statuses[draggedIndex];
    const newStatuses = [...statuses];
    newStatuses.splice(draggedIndex, 1);
    newStatuses.splice(dropIndex, 0, draggedStatus);

    onReorder(newStatuses);
    setDraggedIndex(null);
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {t("admin.failedToLoadItemStatuses")}
      </Alert>
    );
  }

  if (isLoading) {
    return <LinearProgress />;
  }

  if (!data?.statuses || data.statuses.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          {t("admin.noItemStatusesFound")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t("admin.createFirstItemStatus")}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <input
                type="checkbox"
                checked={
                  selectedStatuses.length === data.statuses.length &&
                  data.statuses.length > 0
                }
                ref={(input) => {
                  if (input) {
                    input.indeterminate =
                      selectedStatuses.length > 0 &&
                      selectedStatuses.length < data.statuses.length;
                  }
                }}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
            </TableCell>
            <TableCell>{t("admin.sort")}</TableCell>
            <TableCell>{t("admin.name")}</TableCell>
            <TableCell>{t("admin.displayName")}</TableCell>
            <TableCell>{t("admin.color")}</TableCell>
            <TableCell>{t("admin.status")}</TableCell>
            <TableCell>{t("admin.items")}</TableCell>
            <TableCell>{t("admin.requirements")}</TableCell>
            <TableCell>{t("admin.actions")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.statuses.map((status, index) => (
            <TableRow
              key={status.id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
              sx={{
                cursor: "move",
                opacity: draggedIndex === index ? 0.5 : 1,
                "&:hover": { backgroundColor: "action.hover" },
              }}
            >
              <TableCell padding="checkbox">
                <input
                  type="checkbox"
                  checked={selectedStatuses.includes(status.id)}
                  onChange={(e) =>
                    handleSelectStatus(status.id, e.target.checked)
                  }
                />
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DragIndicatorIcon color="action" />
                  <Typography variant="body2">{status.sortOrder}</Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {status.icon && (
                    <Typography variant="h6" sx={{ color: status.color }}>
                      {status.icon}
                    </Typography>
                  )}
                  <Typography variant="body2" fontWeight="medium">
                    {status.name}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{status.displayName}</Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      backgroundColor: status.color,
                      borderRadius: "50%",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  />
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {status.color}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Chip
                  label={status.isActive ? t("admin.active") : t("admin.inactive")}
                  color={status.isActive ? "success" : "default"}
                  size="small"
                  icon={status.isActive ? <CheckCircleIcon /> : undefined}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {status.orderItemCount || 0}
                </Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  {status.requiresTrackingNumber && (
                    <Chip label={t("admin.tracking")} size="small" color="info" />
                  )}
                  {status.requiresNote && (
                    <Chip label={t("admin.note")} size="small" color="warning" />
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuOpen(e, status.id)}
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl[status.id]}
                  open={Boolean(anchorEl[status.id])}
                  onClose={() => handleMenuClose(status.id)}
                >
                  <MenuItem
                    onClick={() => {
                      onView(status);
                      handleMenuClose(status.id);
                    }}
                  >
                    <VisibilityIcon sx={{ mr: 1 }} />
                    {t("admin.view")}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      onEdit(status);
                      handleMenuClose(status.id);
                    }}
                  >
                    <EditIcon sx={{ mr: 1 }} />
                    {t("admin.edit")}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      onToggleActive(status.id);
                      handleMenuClose(status.id);
                    }}
                  >
                    <CheckCircleIcon sx={{ mr: 1 }} />
                    {status.isActive ? t("admin.deactivate") : t("admin.activate")}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      onDelete(status.id);
                      handleMenuClose(status.id);
                    }}
                    sx={{ color: "error.main" }}
                  >
                    <DeleteIcon sx={{ mr: 1 }} />
                    {t("admin.delete")}
                  </MenuItem>
                </Menu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ItemStatusTable;
